/**
 * Plain-text helpers for the notification bell (spec 03, section 6).
 * The bell never parses Markdown or HTML. Times are Australia/Perth.
 *
 * Status-alert excerpts depend on the email preamble (TYPE: title, then
 * the "Dear <client> Team," greeting). If that plain-text shape changes,
 * update this file and its fixture together.
 */
import { STATE_LABEL, TYPE_META } from "../components/statusAlerts";

export type NotificationContent = {
  event: string;
  subject?: string | null;
  body?: string | null;
  payload_jsonb?: Record<string, unknown> | null;
};

const EXCERPT_LIMIT = 240;
/** A word break earlier than this is ignored and the excerpt is cut at 240. */
const HARD_CUT_FLOOR = 144;

const STATUS_SUBJECT_PREFIXES = [
  "[Service incident] ",
  "[Scheduled Maintenance] ",
  "[Service update] ",
];

const TYPE_LINE = /^[A-Z][A-Z ]+: /;
const GREETING_LINE = /^Dear .+,$/;

const PERTH = "Australia/Perth";
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const perthFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: PERTH,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  weekday: "short",
});

export type NotificationChip = {
  className: string;
  label: string;
  /** Status chips reuse the sa-chip dot. The action-needed badge does not. */
  dot: boolean;
};

export type NotificationMeta = {
  typeLabel: string;
  chip: NotificationChip | null;
};

type PerthStamp = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekday: number;
};

export function paragraphs(body: string): string[] {
  return body
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split(/\n[ \t]*\n+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

function collapseWhitespace(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

/** A hard cut must not leave a lone UTF-16 high surrogate. */
function cutIndex(text: string, cutAt: number): number {
  if (cutAt > 0 && cutAt <= text.length) {
    const previous = text.charCodeAt(cutAt - 1);
    if (previous >= 0xd800 && previous <= 0xdbff) return cutAt - 1;
  }
  return cutAt;
}

/** Collapse whitespace, then cut at a word before `limit` and add an ellipsis. */
export function clampText(text: string, limit = EXCERPT_LIMIT): string {
  const collapsed = collapseWhitespace(text);
  if (collapsed.length <= limit) return collapsed;
  const space = collapsed.slice(0, limit).lastIndexOf(" ");
  const cutAt = cutIndex(collapsed, space >= HARD_CUT_FLOOR ? space : limit);
  const trimmed = collapsed.slice(0, cutAt).replace(/[,;:\s]+$/g, "");
  return `${trimmed}…`;
}

function skipPreamble(parts: string[], event: string): string[] {
  if (event !== "status_alert") return parts;
  let index = 0;
  if (index < parts.length && TYPE_LINE.test(parts[index])) index += 1;
  if (index < parts.length && GREETING_LINE.test(parts[index])) index += 1;
  return parts.slice(index);
}

export function notificationTitle(item: NotificationContent): string {
  const subject = (item.subject ?? "").trim();
  if (!subject) return "Notification";
  if (item.event !== "status_alert") return subject;
  for (const prefix of STATUS_SUBJECT_PREFIXES) {
    if (subject.startsWith(prefix)) {
      return subject.slice(prefix.length).trim() || "Notification";
    }
  }
  return subject;
}

export function notificationExcerpt(item: NotificationContent): string {
  const body = item.body ?? "";
  if (!body.trim()) return "";
  const first = skipPreamble(paragraphs(body), item.event)[0];
  if (!first) return "";
  return clampText(first);
}

/** Body with the status-alert email preamble removed. Line breaks stay. */
export function notificationFullText(item: NotificationContent): string {
  const body = item.body ?? "";
  if (!body.trim()) return "";
  const normalised = body.replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim();
  if (item.event !== "status_alert") return normalised;
  const parts = paragraphs(normalised);
  const rest = skipPreamble(parts, item.event);
  if (rest.length === parts.length) return normalised;
  return rest.join("\n\n").trim();
}

function payloadString(payload: Record<string, unknown> | null | undefined, key: string): string {
  const value = payload?.[key];
  return typeof value === "string" ? value.trim() : "";
}

function titleCaseState(state: string): string {
  return state
    .replace(/[_-]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function notificationMeta(item: NotificationContent): NotificationMeta {
  const payload = item.payload_jsonb ?? {};
  switch (item.event) {
    case "status_alert": {
      const alertType = payloadString(payload, "alert_type");
      const typeLabel = Object.hasOwn(TYPE_META, alertType)
        ? TYPE_META[alertType as keyof typeof TYPE_META].label
        : "Service notice";
      const state = payloadString(payload, "state");
      if (!state) return { typeLabel, chip: null };
      const knownState = Object.hasOwn(STATE_LABEL, state);
      return {
        typeLabel,
        chip: {
          className: knownState ? `sa-chip ${state}` : "sa-chip",
          label: knownState ? STATE_LABEL[state] : titleCaseState(state),
          dot: true,
        },
      };
    }
    case "approval_step_assigned":
      return { typeLabel: "Approval request", chip: null };
    case "approval_nudge":
      return { typeLabel: "Approval reminder", chip: null };
    case "approval_outcome":
      return { typeLabel: "Request update", chip: null };
    case "approval_rejected":
      return { typeLabel: "Request rejected", chip: null };
    case "approval_expired":
      return { typeLabel: "Request expired", chip: null };
    case "support_reply":
      return { typeLabel: "Support reply", chip: null };
    case "approval_ticket_create_failed":
      return {
        typeLabel: "Ticket not created",
        chip: {
          className: "badge badge-critical",
          label: "Action needed",
          dot: false,
        },
      };
    default:
      return { typeLabel: "Notification", chip: null };
  }
}

export function notificationHref(
  item: NotificationContent,
  options: { isStaff: boolean },
): string | null {
  switch (item.event) {
    case "status_alert": {
      if (!options.isStaff) return null;
      const id = payloadString(item.payload_jsonb, "announcement_id");
      return id ? `/admin/announcements/${id}` : null;
    }
    case "approval_step_assigned":
    case "approval_nudge":
      return "/services/approvals";
    case "approval_outcome":
    case "approval_rejected":
    case "approval_expired":
      return "/services/requests";
    case "support_reply": {
      // Opens that conversation in Settings -> Help & Support.
      const id = payloadString(item.payload_jsonb, "support_ticket_id");
      return id
        ? `/me/preferences?support=${encodeURIComponent(id)}`
        : "/me/preferences";
    }
    default:
      return null;
  }
}

function perthStamp(date: Date): PerthStamp | null {
  if (Number.isNaN(date.getTime())) return null;
  const parts = perthFormatter.formatToParts(date);
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? "";
  const weekday = WEEKDAYS.indexOf(read("weekday"));
  const month = Number(read("month"));
  const day = Number(read("day"));
  const year = Number(read("year"));
  const hour = Number(read("hour"));
  const minute = Number(read("minute"));
  if (weekday < 0 || !month || !day || !year || Number.isNaN(hour) || Number.isNaN(minute)) {
    return null;
  }
  return { year, month, day, hour, minute, weekday };
}

function clock(stamp: PerthStamp): string {
  const period = stamp.hour < 12 ? "am" : "pm";
  const hour = stamp.hour % 12 || 12;
  return `${hour}:${String(stamp.minute).padStart(2, "0")} ${period}`;
}

/** Calendar days since the Unix epoch for a Perth year-month-day. */
function perthDayIndex(stamp: PerthStamp): number {
  return Math.floor(Date.UTC(stamp.year, stamp.month - 1, stamp.day) / 86_400_000);
}

export function notificationTime(
  iso: string,
  now: Date = new Date(),
): { label: string; title: string } {
  const stamp = perthStamp(new Date(iso));
  if (!stamp) return { label: "", title: "" };
  const time = clock(stamp);
  const title = `${WEEKDAYS[stamp.weekday]} ${stamp.day} ${MONTHS[stamp.month - 1]} ${stamp.year}, ${time} AWST`;
  const elapsed = now.getTime() - new Date(iso).getTime();
  if (Math.abs(elapsed) < 60_000) return { label: "Just now", title };
  if (elapsed >= 60_000) {
    const minutes = Math.floor(elapsed / 60_000);
    if (minutes < 60) return { label: `${minutes} min ago`, title };
  }
  const nowStamp = perthStamp(now);
  if (nowStamp) {
    const delta = perthDayIndex(nowStamp) - perthDayIndex(stamp);
    if (delta === 0) return { label: `Today, ${time} AWST`, title };
    if (delta === 1) return { label: `Yesterday, ${time} AWST`, title };
  }
  return { label: `${stamp.day} ${MONTHS[stamp.month - 1]}, ${time} AWST`, title };
}
